import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWeb3Auth } from '../../web3/Web3AuthContext';
import {
  EMPTY_REGISTRY_STATS,
  listRegistryApplications,
  summarizeRegistryApplications,
} from '../../web3/registryClient';
import { Users, FileText, Award, Activity } from 'lucide-react';
import Section from '../../components/Section';
import Button from '../../components/Button';


const AdminDashboard = () => {
  const navigate = useNavigate();
  const web3 = useWeb3Auth();
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState(EMPTY_REGISTRY_STATS);
  const allowed = web3.isConnected && web3.isAdmin;

  useEffect(() => {
    if (!web3.isConnected) {
      navigate('/auth');
      return;
    }
    if (!web3.isAdmin) {
      navigate('/');
    }
  }, [navigate, web3.isAdmin, web3.isConnected]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setStatsLoading(true);
    setError(null);
    listRegistryApplications(web3.chainId || 2786)
      .then((rows) => {
        if (cancelled) return;
        setStats(summarizeRegistryApplications(rows));
      })
      .catch((err) => {
        console.error('Admin dashboard error:', err);
        if (cancelled) return;
        setStats(EMPTY_REGISTRY_STATS);
        setError(err instanceof Error ? err.message : 'Failed to load registry stats');
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, web3.chainId]);

  if (!allowed) {
    return (
      <Section className="pt-28">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Section>
    );
  }

  return (
    <Section className="pt-28">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Admin Dashboard</h1>
          <p className="text-gray-600">
            Welcome to the SportsFuture admin dashboard. Manage applications and monitor platform activity.
          </p>
          {statsLoading && (
            <p className="mt-2 text-sm text-gray-500">Loading registry stats…</p>
          )}
          {error && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Could not load on-chain application counts. You can still review applications.
              <span className="mt-1 block font-mono text-xs text-amber-700">{error}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <span className="text-2xl font-bold">{stats.totalAthletes}</span>
            </div>
            <h3 className="text-gray-600">Total Athletes</h3>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center">
                <Award className="h-6 w-6 text-secondary" />
              </div>
              <span className="text-2xl font-bold">{stats.totalAgents}</span>
            </div>
            <h3 className="text-gray-600">Sports Agents</h3>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <Activity className="h-6 w-6 text-primary" />
              </div>
              <span className="text-2xl font-bold">{stats.totalOfficers}</span>
            </div>
            <h3 className="text-gray-600">Sports Officers</h3>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center">
                <FileText className="h-6 w-6 text-secondary" />
              </div>
              <span className="text-2xl font-bold">{stats.pendingApplications}</span>
            </div>
            <h3 className="text-gray-600">Pending Applications</h3>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-6">Quick Actions</h2>
            <div className="space-y-4">
              <Button
                to="/admin/applications"
                variant="primary"
                className="w-full"
              >
                Review Applications
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/admin/users')}
              >
                Manage Users
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-6">Recent Activity</h2>
            <div className="space-y-4">
              <p className="text-gray-600">Coming soon...</p>
            </div>
          </div>
        </div>
      </motion.div>
    </Section>
  );
};

export default AdminDashboard;